import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AxiosError, AxiosResponse } from 'axios';
import { normalize } from 'normalizr';
import { api } from 'libs';
import initialState from 'state/initial-state';
import schema from 'state/schema';
import serializeRequestError from 'state/utils/serialize-request-error';
import type { AppDispatch, AppGetState } from './index';

type BrokerageStatus = {
  lender_id: number;
};

type Brokerage = {
  name: string;
  logo_path: string | null;
  setting_allow_join_requests: boolean;
  statuses: BrokerageStatus[];
};

/**
 * Hydrate Initial State global LS object.
 * @type {Object}
 */
let initialByIdState: Record<string, Brokerage> = {};
const currentUser = initialState.data?.user;

if (currentUser) {
  const user = normalize(currentUser, schema.user);

  initialByIdState = {
    ...initialByIdState,
    ...user.entities.brokerages,
  };
}

/**
 * ById Brokerage Reducer
 * @param {Object} state
 * @param {Object} action
 * @return {Object}
 */
const brokeragesSlice = createSlice({
  name: 'lenderspotlight/brokerages',
  initialState: {
    byId: initialByIdState,
    isLoading: false,
    isUploading: false,
  },
  reducers: {
    fetchStarted: (state) => {
      state.isLoading = true;
    },
    fetchFailed: (state) => {
      state.isLoading = false;
    },
    fetchSucceeded: (state, action) => {
      state.isLoading = false;
      state.byId = {
        ...state.byId,
        ...action.payload.entities.brokerages,
      };
    },
    statusRemoved: (state, action) => {
      const { brokerageId, lender } = action.payload;
      const brokerage = state.byId[brokerageId];
      if (!brokerage) return;

      brokerage.statuses = brokerage.statuses.filter((status) => lender.id !== status.lender_id);
    },
    updateStarted: (state) => {
      state.isLoading = true;
    },
    updateFailed: (state, _action: PayloadAction<{ error: unknown; payload: unknown }>) => {
      state.isLoading = false;
    },
    allowJoinRequestsUpdated: (state, action) => {
      const { brokerageId, value } = action.payload;
      if (!state.byId[brokerageId]) return;

      state.byId[brokerageId].setting_allow_join_requests = value;
    },
    updateSucceeded: (state, action) => {
      state.isLoading = false;
      state.byId = {
        ...state.byId,
        ...action.payload.entities.brokerages,
      };
    },
    statusUpdated: (state, action) => {
      const { brokerageId, status } = action.payload;
      const brokerage = state.byId[brokerageId];
      if (!brokerage) return;

      brokerage.statuses = brokerage.statuses.filter((oldStatus) => status.lender_id !== oldStatus.lender_id);
      brokerage.statuses.push(status);
    },
    uploadStarted: (state, action) => {
      const { id, logoPath } = action.payload;
      state.isUploading = true;
      if (!state.byId[id]) return;
      state.byId[id].logo_path = logoPath;
    },
    uploadFailed: (state, action) => {
      const { id, previousLogoPath } = action.payload;
      state.isUploading = false;
      if (!state.byId[id]) return;
      state.byId[id].logo_path = previousLogoPath;
    },
    uploadSucceeded: (state, action) => {
      const { id, logoPath } = action.payload;
      state.isUploading = false;
      if (!state.byId[id]) return;
      state.byId[id].logo_path = logoPath;
    },
  },
});

export default brokeragesSlice.reducer;

const {
  allowJoinRequestsUpdated,
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  statusRemoved,
  statusUpdated,
  updateFailed,
  updateStarted,
  updateSucceeded,
  uploadFailed,
  uploadStarted,
  uploadSucceeded,
} = brokeragesSlice.actions;

/**
 * Action Types
 * @type {Object}
 */
export const actionTypes = {
  updateFailed: updateFailed.type,
  updateSucceeded: updateSucceeded.type,
};

/**
 * Update Failed Action
 * @param {Error} error
 * @return {Object}
 */
const updateRequestFailed = (error: unknown) => {
  const payload = (error as AxiosError<{ errors?: Record<string, unknown> }>).response?.data?.errors ?? {};
  return updateFailed({ error: serializeRequestError(error), payload });
};

/**
 * Update Success Action
 * @param {*} response
 * @return {*}
 */
const updateSuccess = (response: AxiosResponse) =>
  updateSucceeded({
    brokerageName: response.data.name,
    ...normalize(response.data, schema.brokerage),
  });

/**
 * Remove Status By Brokerage Thunk
 * @param {Number} brokerageId
 * @param {Object} payload
 * @return {Function}
 */
export const removeStatus =
  (brokerageId: string | number, lender: { id: number }) => async (dispatch: AppDispatch, getState: AppGetState) => {
    const previousStatus = getState().brokerages.byId[brokerageId]?.statuses?.find(
      (status) => status.lender_id === lender.id
    );
    const payload = {
      lender,
      brokerageId,
    };

    const data = {
      lender_id: lender.id,
    };

    dispatch(statusRemoved(payload));
    try {
      return await api.delete(`brokerages/${brokerageId}/statuses`, { data });
    } catch (error) {
      if (previousStatus) {
        dispatch(
          statusUpdated({
            brokerageId,
            status: previousStatus,
          })
        );
      }
      throw error;
    }
  };

/**
 * Update Brokerage Thunk
 * @param {Number} brokerageId
 * @param {Object} data
 * @return {Function}
 */
export const update =
  (brokerageId: string | number, data: { description?: string | null; name?: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(updateStarted());

    try {
      const response = await api.put(`brokerages/${brokerageId}`, data);
      return dispatch(updateSuccess(response));
    } catch (error) {
      return dispatch(updateRequestFailed(error));
    }
  };

/**
 * Optimistically update whether the brokerage accepts join requests.
 * @param {Number} brokerageId
 * @param {Boolean} value
 * @return {Function}
 */
export const updateAllowJoinRequests =
  (brokerageId: string | number, value: boolean) => async (dispatch: AppDispatch, getState: AppGetState) => {
    const brokerage = getState().brokerages.byId[brokerageId];
    if (!brokerage) return null;

    const previousValue = brokerage.setting_allow_join_requests;
    dispatch(allowJoinRequestsUpdated({ brokerageId, value }));
    dispatch(updateStarted());

    try {
      const response = await api.put(`brokerages/${brokerageId}`, {
        name: brokerage.name,
        setting_allow_join_requests: value,
      });

      return dispatch(updateSuccess(response));
    } catch (error) {
      dispatch(allowJoinRequestsUpdated({ brokerageId, value: previousValue }));
      return dispatch(updateRequestFailed(error));
    }
  };

/**
 * Update Status By Brokerage Thunk
 * @param {Number} brokerageId
 * @param {Object} payload
 * @return {Function}
 */
export const updateStatus =
  (brokerageId: string | number, status: { id: number; lender_id?: number }) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    const previousStatus = getState().brokerages.byId[brokerageId]?.statuses?.find(
      (oldStatus) => oldStatus.lender_id === status.lender_id
    );
    const payload = {
      status,
      brokerageId,
    };

    const data = {
      status_id: status.id,
      lender_id: status.lender_id,
    };

    dispatch(statusUpdated(payload));
    try {
      return await api.patch(`brokerages/${brokerageId}/statuses`, data);
    } catch (error) {
      if (previousStatus) {
        dispatch(
          statusUpdated({
            brokerageId,
            status: previousStatus,
          })
        );
      } else {
        dispatch(
          statusRemoved({
            brokerageId,
            lender: { id: status.lender_id },
          })
        );
      }
      throw error;
    }
  };

/**
 * Upload User Avatar Thnuk.
 * @param {Number} brokerageId
 * @param {Object} photo
 * @return {Function}
 */
export const uploadPhoto =
  (brokerageId: string | number, photo: File) => async (dispatch: AppDispatch, getState: AppGetState) => {
    const brokerage = getState().brokerages.byId[brokerageId];
    const previousLogoPath = brokerage?.logo_path ?? null;
    const data = new FormData();
    data.append('avatar', photo);

    const readPreview = () =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target?.result ?? null);
        reader.onerror = () => reject(new Error('Failed to read brokerage logo preview'));
        reader.readAsDataURL(photo);
      });

    try {
      const previewUrl = await readPreview();
      dispatch(
        uploadStarted({
          id: brokerageId,
          logoPath: previewUrl,
        })
      );

      try {
        const response = await api.post(`brokerages/${brokerageId}/avatar`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        return dispatch(
          uploadSucceeded({
            id: brokerageId,
            logoPath: response.data?.logo_path ?? response.data?.data?.logo_path ?? previewUrl,
          })
        );
      } catch (error) {
        return dispatch(
          uploadFailed({
            error: serializeRequestError(error),
            id: brokerageId,
            previousLogoPath,
          })
        );
      }
    } catch (error) {
      return dispatch(
        uploadFailed({
          error: serializeRequestError(error),
          id: brokerageId,
          previousLogoPath,
        })
      );
    }
  };
